import React, { useEffect, useState } from 'react';
import { ExternalLink, X } from 'lucide-react';

const LinkBanner: React.FC = () => {
  const [link, setLink] = useState<string | null>(null);
  const [countdown, setCountdown] = useState<number>(15);
  const [isCancelled, setIsCancelled] = useState<boolean>(false);

  useEffect(() => {
    // Get the link parameter from the URL
    const urlParams = new URLSearchParams(window.location.search);
    const linkParam = urlParams.get('link');

    if (linkParam) {
      // Validate the link - must be a properly formatted URL
      try {
        const url = new URL(linkParam);
        // Ensure the protocol is either http or https for security
        if (url.protocol === 'http:' || url.protocol === 'https:') {
          setLink(linkParam);
        } else {
          console.warn('Invalid protocol in link parameter:', url.protocol);
        }
      } catch (error) {
        console.warn('Invalid URL in link parameter:', linkParam);
      }
    }
  }, []);

  useEffect(() => {
    if (!link || isCancelled) return;

    // Countdown timer
    if (countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else {
      // Redirect when countdown reaches 0
      window.location.href = link;
    }
  }, [countdown, link, isCancelled]);

  const handleCancel = () => {
    setIsCancelled(true);
    // Track cancellation in analytics if available
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('event', 'link_redirect_cancelled', {
        event_category: 'engagement',
        event_label: 'banner_interaction'
      });
    }
  };

  if (link == null || isCancelled) {
    return null;
  }

  return (
    <div className="bg-yellow-400 text-gray-900 py-3 px-4 flex justify-center items-center relative">
      <div className="container mx-auto flex flex-col sm:flex-row justify-between items-center gap-2">
        <div className="flex items-center flex-wrap justify-center sm:justify-start">
          <span className="mr-2 font-medium">Shared link:</span>
          <a 
            href={link}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-800 hover:text-blue-600 underline flex items-center"
            onClick={() => {
              // Track manual link click in analytics
              if (typeof window !== 'undefined' && (window as any).gtag) {
                (window as any).gtag('event', 'shared_link_clicked', {
                  event_category: 'engagement',
                  event_label: 'banner_manual_click'
                });
              }
            }}
          >
            {link.length > 60 ? `${link.substring(0, 60)}...` : link}
            <ExternalLink className="ml-1 w-4 h-4" />
          </a>
        </div>
        
        {/* Countdown Timer with Blinking Animation */}
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium">Redirecting in:</span>
            <span 
              className={`text-2xl font-bold ${
                countdown <= 3 ? 'animate-pulse text-red-700' : 'animate-blink'
              }`}
            >
              {countdown}s
            </span>
          </div>
          
          {/* Cancel Button */}
          <button
            onClick={handleCancel}
            className="bg-gray-900 hover:bg-gray-800 text-white p-1.5 rounded-full transition-colors flex items-center justify-center"
            aria-label="Cancel redirect"
            title="Cancel automatic redirect"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default LinkBanner;